"use client";

import React, { useEffect } from "react";
import { cn } from "@/lib/utils";
import { useMobileMenu } from "@/contexts/frontend/mobile-menu-context";

const panelStyle = { top: "var(--header-height)", height: "calc(100vh - var(--header-height))" };

const positionClassByOpenFrom = {
	left: "left-0 w-full",
	right: "right-0 w-full",
	top: "left-0 right-0 w-full",
};

const openTransformClassByOpenFrom = {
	left: "translate-x-0",
	right: "translate-x-0",
	top: "translate-y-0",
};

const closedTransformClassByOpenFrom = {
	left: "-translate-x-full",
	right: "translate-x-full",
	top: "-translate-y-[calc(100%+var(--topbar-height,0px))]",
};

interface MobileNavigationPanelProps {
	children: React.ReactNode;
	submenus: React.ReactNode;
	openFrom: "left" | "right" | "top";
	className: string;
	contentClassName: string;
}

/** Displays the mobile navigation with separate overlays for active submenus. */
export function MobileNavigationPanel({ children, submenus, openFrom, className, contentClassName }: MobileNavigationPanelProps) {
	const { mobileOpen, setMobileOpen, expandedIds, setExpandedIds } = useMobileMenu() ?? {};

	useEffect(() => {
		if (!mobileOpen) {
			setExpandedIds?.([]);
			return;
		}

		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = previousOverflow;
		};
	}, [mobileOpen, setExpandedIds]);

	return (
		<>
			<div
				aria-hidden={!mobileOpen}
				className={cn(
					"fixed left-0 right-0 bottom-0 z-40 bg-black/30 transition-opacity duration-300",
					mobileOpen ? "opacity-100" : "pointer-events-none opacity-0",
				)}
				style={panelStyle}
				onClick={() => setMobileOpen?.(false)}
			/>
			<aside
				inert={!mobileOpen}
				aria-modal="true"
				aria-hidden={!mobileOpen}
				role="dialog"
				className={cn(
					"fixed z-50 flex flex-col overflow-hidden transition-transform",
					positionClassByOpenFrom[openFrom],
					className,
					mobileOpen && "shadow-lg",
					mobileOpen ? openTransformClassByOpenFrom[openFrom] : closedTransformClassByOpenFrom[openFrom],
				)}
				style={panelStyle}
			>
				<div inert={!!expandedIds?.length} aria-hidden={!!expandedIds?.length} className={cn("flex flex-col p-4 overflow-auto", contentClassName)}>
					{children}
				</div>
				{submenus}
			</aside>
		</>
	);
}
